package org.moxultimate.topwithmox.productcrud.add

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.crud.*
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDState
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDViewModel
import org.moxultimate.topwithmox.productcrud.main.ProductFormEntry
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * ProductItemAddPage
 * Full-page form for adding a new product entry to a given product content.
 * Calls POST /products with [contentId] and the form fields on submit.
 *
 * @param contentId The parent content ID this product belongs to.
 */
@Composable
fun ProductItemAddPage(contentId: Long) {
    val viewModel: ProductCRUDViewModel = koinViewModel()
    val productOpState by viewModel.productOpState.collectAsState()
    val navigator = rememberAppNavigator()

    var entry by remember { mutableStateOf(ProductFormEntry()) }
    var submitAttempted by remember { mutableStateOf(false) }

    if (productOpState is ProductCRUDState.Success) {
        CRUDSuccessDialog(
            message = (productOpState as ProductCRUDState.Success).message,
            onOkay = {
                viewModel.resetProductState()
                navigator.goToProductCRUD()
            },
        )
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 700.px, value = 70.vw, max = 1100.px))
                .padding(top = 80.px, bottom = 48.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(28.px),
        ) {
            // Title
            SpanText(
                text = "Add Product",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .moxTextGradient(),
            )

            // Error banner
            if (productOpState is ProductCRUDState.Error) {
                CRUDStatusBanner(
                    message = (productOpState as ProductCRUDState.Error).message,
                    isError = true,
                    onDismiss = { viewModel.resetProductState() },
                )
            }

            // Validation hint
            if (submitAttempted && !entry.isValid) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 12.px, topBottom = 8.px)
                        .borderRadius(8.px)
                        .backgroundColor(Colors.MistyRose)
                        .border(1.px, LineStyle.Solid, Colors.LightCoral),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        text = "Missing required: ${entry.missingFields.joinToString(" · ")}",
                        modifier = Modifier.fontFamily(FontFamily).fontSize(12.px).color(Colors.Crimson),
                    )
                }
            }

            ProductFormFields(entry = entry, onEntryChange = { entry = it })

            val isLoading = productOpState is ProductCRUDState.Loading
            GradientButton(
                text = if (isLoading) "Creating…" else "Add Product",
                disabled = isLoading,
                onClick = {
                    submitAttempted = true
                    if (entry.isValid && !isLoading) viewModel.createProductItem(contentId, entry)
                },
            )
        }
    }
}

// ProductFormFields and FormSection are defined in ProductAddPage.kt (same package).
